import { computed } from '@angular/core';
import {
  signalStoreFeature,
  type,
  withComputed,
  withMethods,
} from '@ngrx/signals';
import { ComparisonValueState } from './comparison-value.state';
import { StateFilter } from '../crud.state';
import { ComparisonValue } from '../../models/geo-data';

const defaultItems = [
  {
    _id: 'avg',
    key: 'avg',
    name: 'Durchschnitt',
  },
  {
    _id: 'total',
    key: 'total',
    name: 'Gesamt',
  },
];

const filterItems = (items: ComparisonValue[], filter: StateFilter) => {
  return items.filter(item => {
    const nameMatch = item.name
      .toLowerCase()
      .includes(filter.query.toLowerCase());
    const keyMatch = item.key
      .toLowerCase()
      .includes(filter.query.toLowerCase());
    return nameMatch && keyMatch;
  });
};

export function withComparisonValueSelectors() {
  return signalStoreFeature(
    {
      state: type<ComparisonValueState>(),
    },
    withComputed(({ items, filter, selection }) => ({
      count: computed(() => Object.keys(items()).length),
      byKey: computed(() => {
        const cVals: Record<string, ComparisonValue> = {};
        [...defaultItems, ...Object.values(items())].forEach(item => {
          cVals[item.key] = item;
        });
        return cVals;
      }),
      keys: computed(() => {
        return [
          ...Object.values(items()).map(val => val.key),
          ...defaultItems.map(item => item.key),
        ];
      }),
      keysWithoutDefaults: computed(() => {
        return Object.values(items()).map(val => val.key);
      }),
      asArray: computed(() => {
        return defaultItems.concat(
          Object.values(items())
            .filter(value => value.commune !== null)
            .sort((a, b) => (a.name < b.name ? -1 : 1)) as ComparisonValue[]
        );
      }),
      getDefaults: computed(() => {
        return defaultItems;
      }),
      getDefaultsAsKeyValue: computed(() => {
        const defaultKeyValue: { [key: string]: any } = {};
        defaultItems.forEach(defaultItem => {
          defaultKeyValue[defaultItem.key] = defaultItem;
        });
        return defaultKeyValue;
      }),
      withoutDefault: computed(() => {
        return items();
      }),
      withoutDefaultAsArray: computed(() => {
        return Object.values(items());
      }),
      filteredSortedByName: computed(() => {
        let filtered = Object.values(items());
        if (filter()) {
          filtered = filterItems(filtered, filter());
        }
        return filtered.sort((a, b) => (a.name < b.name ? -1 : 1));
      }),
      filteredSelection: computed(() => {
        let filtered = selection();
        if (filter()) {
          filtered = filterItems(filtered, filter());
        }
        return filtered as ComparisonValue[];
      }),
    })),
    withMethods(store => ({
      getNameByKey(key: string) {
        const items = store.asArray();
        return items.filter(item => item.key === key)[0].name;
      },
    }))
  );
}
